import { useMutation } from '@apollo/client';
import type {
  InsertAssessmentMutation,
  InsertComplianceMonitoringAssessmentMutation,
  InsertInternalAuditReportMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetAllComplianceMonitoringAssessmentsDocument,
  GetAssessmentByIdDocument,
  GetAssessmentsDocument,
  GetComplianceMonitoringAssessmentByIdDocument,
  GetInternalAuditReportByIdDocument,
  InsertAssessmentDocument,
  InsertComplianceMonitoringAssessmentDocument,
  InsertInternalAuditReportDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerAndContributorIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';

import { evictField } from '@/utils/graphqlUtils';

import AssessmentForm from '../../forms/assessment-form';
import type { AssessmentFormDataFields } from '../../forms/assessment-form/assessmentSchema';
import type { AssessmentTypeEnum } from '../../useAssessmentTypeConfig';
import { useAssessmentTypeConfig } from '../../useAssessmentTypeConfig';

interface Props {
  assessmentMode: AssessmentTypeEnum;
}

const getMutationDocument = (assessmentMode: AssessmentTypeEnum) => {
  switch (assessmentMode) {
    case 'rating':
      return InsertAssessmentDocument;
    case 'compliance_monitoring_assessment':
      return InsertComplianceMonitoringAssessmentDocument;
    case 'internal_audit_report':
      return InsertInternalAuditReportDocument;
  }
};

const mapData = (
  data:
    | InsertAssessmentMutation
    | InsertComplianceMonitoringAssessmentMutation
    | InsertInternalAuditReportMutation
    | null
    | undefined
) => {
  if ((data as InsertAssessmentMutation)?.insertAssessmentApi) {
    return (data as InsertAssessmentMutation).insertAssessmentApi;
  }
  if (
    (data as InsertComplianceMonitoringAssessmentMutation)
      ?.insertComplianceMonitoringAssessmentApi
  ) {
    return (data as InsertComplianceMonitoringAssessmentMutation)
      .insertComplianceMonitoringAssessmentApi;
  }
  if (
    (data as InsertInternalAuditReportMutation)?.insertInternalAuditReportApi
  ) {
    return (data as InsertInternalAuditReportMutation)
      .insertInternalAuditReportApi;
  }
};

const AssessmentCreateTab: FC<Props> = ({ assessmentMode }) => {
  const navigate = useNavigate();
  const {
    routing: { detailsUrl },
  } = useAssessmentTypeConfig(assessmentMode);
  const mutationDocument = getMutationDocument(assessmentMode);
  const [mutate] = useMutation(mutationDocument, {
    update: (cache) => {
      evictField(cache, 'assessment');
      evictField(cache, 'assessment_aggregate');
      evictField(cache, 'internal_audit_report');
      evictField(cache, 'internal_audit_report_aggregate');
      evictField(cache, 'compliance_monitoring_assessment');
      evictField(cache, 'compliance_monitoring_assessment_aggregate');
    },
    refetchQueries: [
      GetAssessmentByIdDocument,
      GetAssessmentsDocument,
      GetInternalAuditReportByIdDocument,
      GetInternalAuditReportByIdDocument,
      GetComplianceMonitoringAssessmentByIdDocument,
      GetAllComplianceMonitoringAssessmentsDocument,
    ],
  });

  const onSave = async (variables: AssessmentFormDataFields) => {
    const { data } = await mutate({
      variables: {
        object: {
          CustomAttributeData: variables.CustomAttributeData || undefined,
          ...ownerAndContributorIds(variables),
          TagTypeIds: variables.tags?.map((t) => t.TagTypeId) || [],
          DepartmentTypeIds:
            variables.departments?.map((d) => d.DepartmentTypeId) || [],
          CompletedByUser: variables.CompletedByUser?.value ?? null,
          Status: variables.Status,
          StartDate: variables.StartDate,
          Title: variables.Title,
          Summary: variables.Summary,
          ActualCompletionDate: variables.ActualCompletionDate,
          NextTestDate: variables.NextTestDate,
          TargetCompletionDate: variables.TargetCompletionDate,
          Outcome: variables.Outcome,
          OriginatingItemId: null,
        },
      },
    });
    const result = mapData(data);
    if (result?.Id) {
      navigate(detailsUrl(result.Id), { replace: true });
    }
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(-1);
    }
  };

  return (
    <AssessmentForm
      onSave={onSave}
      onDismiss={onDismiss}
      assessmentMode={assessmentMode}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
    />
  );
};

export default AssessmentCreateTab;
